import { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Box } from '@mui/material'
import { useProjects } from '../../contexts/ProjectContext'
import { apiClient } from '../../utils/apiClient'
import type { ResourceSidebarContext } from '../../hooks/useResourceSidebarContext'
import { useDatabaseSidebarMeta } from '../../hooks/useDatabaseSidebarMeta'
import {
  ContextSidebarHeader,
  SidebarScrollList,
  UserSidebarFooter,
} from './sidebarPrimitives'
import ProjectContextNav from './ProjectContextNav'
import EnvironmentContextNav from './EnvironmentContextNav'
import DatabaseContextNav from './DatabaseContextNav'
import SyntheticDatabaseContextNav from './SyntheticDatabaseContextNav'
import OrganizationContextNav from './OrganizationContextNav'
import { environmentDetailPath } from '../../utils/environmentSettingsNav'
import { useOrganizationSettings } from '../../hooks/useOrganizationSettings'
import { getEffectiveConnectionMode } from '../../utils/databaseConnectionMode'
import { databaseProjectExitPath, readLastProjectId } from '../../utils/databaseProjectExit'

export default function ResourceContextSidebar({
  context,
  onNavigate,
  showFooter = true,
}: {
  context: Exclude<ResourceSidebarContext, { mode: 'global' }>
  onNavigate?: () => void
  showFooter?: boolean
}) {
  const navigate = useNavigate()
  const location = useLocation()
  const { projects } = useProjects()
  const { organizationName, loading: organizationLoading } = useOrganizationSettings()
  const [fetchedProjectName, setFetchedProjectName] = useState<string | null>(null)
  const [fetchedEnvName, setFetchedEnvName] = useState<string | null>(null)
  const [fetchedEnvUrl, setFetchedEnvUrl] = useState<string | null>(null)

  const projectId =
    context.mode === 'project' || context.mode === 'environment' ? context.projectId : null
  const envId =
    context.mode === 'environment' || context.mode === 'project' ? context.envId : null
  const environmentProjectId = context.mode === 'environment' ? context.projectId : null
  const environmentEnvId = context.mode === 'environment' ? context.envId : null
  const databaseId = context.mode === 'database' ? context.databaseId : null

  const cachedProject = projectId
    ? projects.find((p) => String(p.id) === projectId)
    : undefined

  const { database, loading: databaseLoading } = useDatabaseSidebarMeta(databaseId ?? '')

  useEffect(() => {
    if (!environmentProjectId || !environmentEnvId) {
      setFetchedEnvName(null)
      setFetchedEnvUrl(null)
      return
    }
    let cancelled = false
    let interval: number | undefined
    setFetchedEnvName(null)
    setFetchedEnvUrl(null)
    const load = () => {
      apiClient
        .get<{ changeset_id?: string; slug?: string; url?: string | null }>(
          `/api/projects/${environmentProjectId}/environments/${environmentEnvId}`,
        )
        .then((data) => {
          if (cancelled) return
          const name = data?.changeset_id?.trim() || data?.slug?.trim() || `env-${environmentEnvId}`
          const url = data?.url?.trim() || null
          setFetchedEnvName(name)
          setFetchedEnvUrl(url)
          if (url && interval) {
            window.clearInterval(interval)
            interval = undefined
          }
        })
        .catch(() => {
          if (!cancelled) {
            setFetchedEnvName(`env-${environmentEnvId}`)
            setFetchedEnvUrl(null)
          }
        })
    }
    load()
    interval = window.setInterval(load, 5000)
    return () => {
      cancelled = true
      if (interval) window.clearInterval(interval)
    }
  }, [environmentProjectId, environmentEnvId])

  useEffect(() => {
    if (context.mode !== 'project' || !projectId || cachedProject) {
      setFetchedProjectName(null)
      return
    }
    let cancelled = false
    apiClient
      .get<{ name?: string }>(`/api/projects/${projectId}`)
      .then((data) => {
        if (!cancelled) setFetchedProjectName(data?.name ?? null)
      })
      .catch(() => {
        if (!cancelled) setFetchedProjectName(null)
      })
    return () => {
      cancelled = true
    }
  }, [context.mode, projectId, cachedProject])

  const isSyntheticDatabase =
    context.mode === 'database' &&
    ((database != null && getEffectiveConnectionMode(database) === 'synthetic') ||
      context.exitPath === '/synthetic-databases')

  const exitPath =
    context.mode === 'database'
      ? databaseProjectExitPath({
          synthetic: isSyntheticDatabase,
          attachedProjectIds: databaseLoading ? undefined : database?.attachedProjectIds,
          lastProjectId: readLastProjectId(),
          loading: databaseLoading,
        })
      : context.exitPath

  const projectName =
    context.mode === 'project'
      ? cachedProject?.name ?? fetchedProjectName ?? ''
      : ''
  const overviewPath = context.mode === 'project' && projectId ? `/project/${projectId}` : ''
  const environmentPath =
    context.mode === 'environment' ? environmentDetailPath(context.projectId, context.envId) : ''
  const isProjectOverview =
    context.mode === 'project' &&
    Boolean(overviewPath) &&
    (location.pathname === overviewPath || location.pathname === `${overviewPath}/`)
  const isEnvironmentOverview =
    context.mode === 'environment' &&
    Boolean(environmentPath) &&
    (location.pathname === environmentPath || location.pathname === `${environmentPath}/`)

  const title =
    context.mode === 'organization'
      ? organizationName || 'Organization'
      : context.mode === 'environment'
      ? fetchedEnvName
        ? `${fetchedEnvName} Environment`
        : ''
      : context.mode === 'project'
      ? projectName
        ? `${projectName} Project`
        : ''
      : database?.name ?? ''

  const titleLoading =
    context.mode === 'organization'
      ? organizationLoading && !organizationName
      : context.mode === 'environment'
      ? !fetchedEnvName
      : context.mode === 'project'
      ? !cachedProject?.name && !fetchedProjectName && Boolean(projectId)
      : databaseLoading

  const titleClickPath =
    context.mode === 'environment' ? environmentPath : context.mode === 'project' ? overviewPath : ''
  const titleActive = context.mode === 'environment' ? isEnvironmentOverview : isProjectOverview

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        pt: 0.5,
        pb: 1,
        px: 0,
        overflow: 'hidden',
      }}
    >
      <ContextSidebarHeader
        onExit={() => {
          navigate(exitPath)
          onNavigate?.()
        }}
        onHome={onNavigate}
        title={title}
        loading={titleLoading}
        onTitleClick={
          titleClickPath
            ? () => {
                navigate(titleClickPath)
                onNavigate?.()
              }
            : undefined
        }
        titleActive={titleActive}
        visitHref={context.mode === 'environment' ? fetchedEnvUrl : undefined}
      />
      <SidebarScrollList>
        {context.mode === 'organization' && (
          <OrganizationContextNav onNavigate={onNavigate} />
        )}
        {context.mode === 'environment' && (
          <EnvironmentContextNav
            projectId={context.projectId}
            envId={context.envId}
            onNavigate={onNavigate}
          />
        )}
        {context.mode === 'project' && projectId && (
          <ProjectContextNav
            projectId={projectId}
            envId={envId}
            onNavigate={onNavigate}
          />
        )}
        {context.mode === 'database' &&
          (databaseLoading ? (
            context.exitPath === '/synthetic-databases' ? (
              <SyntheticDatabaseContextNav databaseId={context.databaseId} onNavigate={onNavigate} />
            ) : null
          ) : isSyntheticDatabase ? (
            <SyntheticDatabaseContextNav databaseId={context.databaseId} onNavigate={onNavigate} />
          ) : (
            <DatabaseContextNav databaseId={context.databaseId} onNavigate={onNavigate} />
          ))}
      </SidebarScrollList>
      {showFooter && <UserSidebarFooter />}
    </Box>
  )
}
